import React, { forwardRef, useState } from "react";

import { pressPoint } from "./glass";
import renderIcon from "./renderIcon";

const cx = (...a) => a.filter(Boolean).join(" ");

/**
 * ButtonUI — singurul buton al aplicației. Înlocuiește pastila (Button,
 * createReservationPopup__btn…), butonul cu iconiță mare (BubbleButton, M3Link,
 * sign__button), butonul plat (FlatButton, confirmDialog__btn…), butonul compact
 * (ToolButton, exam-admin__action-btn…), IconButton, ConfirmDeleteButton și
 * iconițele „copiază”, Favorit / Important. Stilurile: scss/UI/_buttonUI.scss.
 *
 * variant: culoarea — neutral (black-p) | subtle (black-s) | primary (accent) |
 *          success (green-l) | success-soft (green-t) | info (blue-l) | info-soft (blue-t) |
 *          danger (red-t) | danger-strong (red-l) | ghost (fără fundal, text white-t)
 * size: sm (compact: uneltele de admin, widget-uri) | md (implicit: pastila) |
 *       lg (iconița de 35px lângă text: login, Programare nouă)
 * icon: nume UIIcon / svg importat / element · iconEnd: iconița după text ·
 *       iconFlip: întoarce iconița (săgeata „Log in →”)
 * Fără text, doar cu icon, e butonul-iconiță: dă-i aria-label.
 * block: pe toată lățimea · pressed: apăsat / ales (aria-pressed, accent)
 * loading: rotița în locul iconiței, butonul nu se mai apasă („Se salvează…”)
 * confirm: textul confirmării („Da, șterge”) — primul clic arată confirmarea și
 *          „Nu” (cancelLabel); onClick rulează abia la confirmare. confirmTitle: titlul
 *          confirmării (ex. ACCOUNT_DELETE_CONFIRM_TITLE). Controlat: confirming +
 *          onConfirmingChange(bool) (un singur rând dintr-o listă în confirmare)
 * as: alt element ("a", Link) — atunci props-urile lui (href, to) trec mai departe;
 *     dezactivat sau în încărcare nu mai navighează
 */
const ButtonUI = forwardRef(function ButtonUI(
   {
      as: Component = "button",
      variant = "neutral",
      size = "md",
      icon,
      iconEnd = false,
      iconFlip = false,
      block = false,
      pressed,
      loading = false,
      confirm,
      confirmTitle,
      confirming: confirmingProp,
      onConfirmingChange,
      cancelLabel = "Nu",
      disabled = false,
      onClick,
      onPointerDown,
      className = "",
      type,
      children,
      ...rest
   },
   ref,
) {
   const [confirmingLocal, setConfirmingLocal] = useState(false);
   const controlled = typeof confirmingProp === "boolean";
   const confirming = controlled ? confirmingProp : confirmingLocal;
   const setConfirming = (next) => {
      if (!controlled) setConfirmingLocal(next);
      onConfirmingChange?.(next);
   };

   const isButton = Component === "button";
   const inactive = disabled || loading;
   const iconOnly = children == null || children === false;
   // rotița stă în cutia iconiței (aceeași mărime), ca butonul să nu se miște
   const iconEl = loading ? (
      <span className="buttonUI__icon buttonUI__icon--spinner" aria-hidden="true">
         <span className="buttonUI__spinner" />
      </span>
   ) : (
      renderIcon(icon, "buttonUI__icon")
   );
   const label = iconOnly ? null : size === "lg" ? <span className="buttonUI__label">{children}</span> : children;
   const sizeClass = size !== "md" && `buttonUI--${size}`;

   // confirmarea pe loc (ConfirmDeleteButton): confirmarea în culoarea butonului și „Nu”
   if (confirm && confirming) {
      return (
         <span className={cx("buttonUIConfirm", block && "buttonUIConfirm--block")}>
            <button
               ref={ref}
               type="button"
               className={cx("buttonUI", variant !== "neutral" && `buttonUI--${variant}`, sizeClass, className)}
               disabled={disabled}
               title={confirmTitle}
               onClick={(e) => {
                  setConfirming(false);
                  onClick?.(e);
               }}
               autoFocus
            >
               {confirm}
            </button>
            <button type="button" className={cx("buttonUI", "buttonUI--subtle", sizeClass)} onClick={() => setConfirming(false)}>
               {cancelLabel}
            </button>
         </span>
      );
   }

   const handleClick = (e) => {
      if (inactive) {
         // linkurile nu au disabled: nu navighează
         if (!isButton) e.preventDefault();
         return;
      }
      if (confirm) setConfirming(true);
      else onClick?.(e);
   };

   return (
      <Component
         ref={ref}
         type={isButton ? type || "button" : type}
         className={cx(
            "buttonUI",
            variant !== "neutral" && `buttonUI--${variant}`,
            sizeClass,
            block && "buttonUI--block",
            iconOnly && "buttonUI--iconOnly",
            iconEnd && "buttonUI--iconEnd",
            iconFlip && "buttonUI--iconFlip",
            pressed && "is-pressed",
            loading && "is-loading",
            className,
         )}
         disabled={isButton ? inactive : undefined}
         aria-disabled={!isButton && inactive ? true : undefined}
         tabIndex={!isButton && inactive ? -1 : undefined}
         aria-pressed={pressed == null ? undefined : Boolean(pressed)}
         aria-busy={loading || undefined}
         onClick={handleClick}
         onPointerDown={(e) => {
            pressPoint(e); // lumina de la apăsare pornește din punctul atins
            onPointerDown?.(e);
         }}
         {...rest}
      >
         {!iconEnd && iconEl}
         {label}
         {iconEnd && iconEl}
      </Component>
   );
});

/**
 * ButtonUI.Row — rândul de butoane de la capătul unui popup sau formular
 * (createReservationPopup__actions-row, questionCategoriesPopup__actions,
 * personalCreateUI__footer…: 17 clase în 21 de fișiere).
 *
 * align: fill (implicit: coloane egale, câte butoane sunt; un buton singur ia tot rândul) |
 *        split (primul — sau `leading` — în stânga, restul în dreapta) |
 *        center (centrate, se rup pe rânduri) | end | start
 * stackOnMobile: cu 3+ coloane egale, pe telefon unul sub altul (implicit da)
 * Coloanele se numără după copiii direcți: un Fragment cu mai multe butoane e o coloană.
 */
function ButtonRow({ align = "fill", leading, stackOnMobile = true, className = "", style, children, ...rest }) {
   const count = React.Children.toArray(children).filter(Boolean).length;
   const fill = align === "fill";
   const split = align === "split" && leading;

   return (
      <div
         className={cx(
            "buttonUIRow",
            `buttonUIRow--${align}`,
            fill && count >= 3 && stackOnMobile && "buttonUIRow--stackMobile",
            className,
         )}
         style={fill ? { "--buttonUIRow-cols": Math.max(1, count), ...style } : style}
         {...rest}
      >
         {split && <div className="buttonUIRow__start">{leading}</div>}
         {children}
      </div>
   );
}

ButtonUI.Row = ButtonRow;

export default ButtonUI;
